import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Container, Typography, Box, CircularProgress, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Select, FormControl, InputLabel, Link
} from "@mui/material";
import { motion } from "framer-motion";
import { getServerPlayers } from "../http/ServerAPI";
import { observer } from "mobx-react-lite";
import BreadCrumbs from "../components/Breadcrumbs";
import { SERVERS_ROUTE } from "../utils/consts";
import dayjs from "dayjs";
import CircleIcon from "@mui/icons-material/Circle";

const sortFields = [
    { value: "game_times", label: "Игровое время" },
    { value: "name", label: "Имя" },
    { value: "steam_id", label: "Steam ID" },
    { value: "created_at", label: "Первая игра" },
    { value: "updated_at", label: "Был в игре" },
];

const PlayersPage = observer(() => {
    const { serverId } = useParams();
    const [players, setPlayers] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("updated_at");

    const hrefs = [{ href: SERVERS_ROUTE, name: "Список серверов" }];

    useEffect(() => {
        const fetchPlayers = async () => {
            try {
                const res = await getServerPlayers(serverId);
                setPlayers(res || []);
            } catch (e) {
                console.error("Ошибка загрузки игроков", e);
            } finally {
                setLoading(false);
            }
        };
        fetchPlayers();
    }, [serverId]);

    useEffect(() => {
        const lower = search.toLowerCase();
        let result = players.filter(p =>
            p.name?.toLowerCase().includes(lower) ||
            p.steam_id?.toLowerCase().includes(lower)
        );

        result = [...result].sort((a, b) => {
            if (sortBy === "game_times") return b.game_times - a.game_times;
            return String(b[sortBy] || "").localeCompare(String(a[sortBy] || ""));
        });

        setFiltered(result);
    }, [search, sortBy, players]);

    const isActive = (updatedAt) => {
        if (!updatedAt) return false;
        return dayjs().diff(dayjs(updatedAt), "minute") <= 15;
    };

    const isOldPlayer = (updatedAt) => {
        if (!updatedAt) return false;
        return dayjs().diff(dayjs(updatedAt), "month") >= 1;
    };

    const getTextStyle = (player) => {
        let color = "inherit";
        if (player.auth_name && player.auth_id) {
            color = "#97eca8";
        } else if (isOldPlayer(player.updated_at)) {
            color = "gray";
        } else if (player.game_times < 1000) {
            color = "#ecd897"; // темно-жёлтый
        }
        return { color };
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs={hrefs} name="Список игроков сервера" />
            </Box>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Typography variant="h4" gutterBottom mt={3}>
                    Список игроков сервера
                </Typography>

                <Box display="flex" gap={2} mt={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <TextField
                        label="Поиск по нику или Steam ID"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Сортировка</InputLabel>
                        <Select
                            value={sortBy}
                            label="Сортировка"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            {sortFields.map((f) => (
                                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Steam ID</TableCell>
                                    <TableCell>Имя</TableCell>
                                    <TableCell>Игровое время</TableCell>
                                    <TableCell>Первая игра</TableCell>
                                    <TableCell>Был в игре</TableCell>
                                    <TableCell>Ник на сайте</TableCell>
                                    <TableCell>Auth ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((player, index) => {
                                    const style = getTextStyle(player);
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Link
                                                    href={`https://steamcommunity.com/profiles/${player.steam_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    color="primary"
                                                >
                                                    {player.steam_id}
                                                </Link>
                                            </TableCell>
                                            <TableCell sx={style}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {isActive(player.updated_at) && (
                                                        <CircleIcon fontSize="small" sx={{ color: "green", fontSize: 12 }} />
                                                    )}
                                                    {player.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={style}>{player.game_times}</TableCell>
                                            <TableCell sx={style}>{player.created_at}</TableCell>
                                            <TableCell sx={style}>{player.updated_at}</TableCell>
                                            <TableCell sx={style}>{player.auth_name || "-"}</TableCell>
                                            <TableCell sx={style}>{player.auth_id || "-"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </motion.div>
        </Container>
    );
});

export default PlayersPage;
